import {t} from './i18n.js';
import {textHtml,fileHtml} from './markdown.js';
import {policy} from './policy.js';
import DOMPurify from 'dompurify';

// Keep a paragraph's last two words together when they fit. This changes only
// inline layout: copied text and UTF-16 annotation/audio offsets stay identical.
export function keepParagraphEndings(root) {
  const doc=root.ownerDocument;
  for(const block of root.querySelectorAll('p,li:not(:has(p))')) {
    if(block.querySelector('.paragraph-ending')) continue;
    // These scripts do not use spaces as general word boundaries. Their
    // language-specific line-breaking rules remain in charge.
    if(/^(?:zh|ja|th|lo|km|my)(?:-|$)/i.test(block.closest('[lang]')?.lang || '')) continue;
    const match=block.textContent.match(/\S+[ \t\n]+\S+\s*$/u);
    if(!match || !/\p{L}/u.test(match[0])) continue;
    const walker=doc.createTreeWalker(block,4);
    const nodes=[];let offset=0,node;
    while((node=walker.nextNode())) {nodes.push({node,start:offset,end:offset+node.length});offset+=node.length;}
    const start=match.index,end=start+match[0].trimEnd().length;
    const touched=nodes.filter(n=>n.end>start && n.start<end);
    // Preserve code, explicit breaks, footnote references and named inline
    // anchors. Extracting a partial named element would duplicate its ID.
    if(!touched.length || touched.some(n=>{
      if(n.node.parentElement.closest('p,li')!==block) return true;
      for(let el=n.node.parentElement;el && el!==block;el=el.parentElement)
        if(el.matches('code,pre,kbd,sup,sub,[id],[lang]')) return true;
      return false;
    })) continue;
    const first=touched[0],last=touched.at(-1),range=doc.createRange();
    range.setStart(first.node,start-first.start);range.setEnd(last.node,end-last.start);
    if(range.cloneContents().querySelector('br')) continue;
    const span=doc.createElement('span');span.className='paragraph-ending';
    span.append(range.extractContents());range.insertNode(span);
  }
}
export function safeHtml(html, base, prepareImage) {
  const clean = DOMPurify.sanitize(html, { USE_PROFILES:{html:true,svg:true}, ADD_TAGS:['iframe'], FORBID_TAGS:['script','object','embed','form','input','button','style','link','meta'], FORBID_ATTR:['style','srcset'], RETURN_DOM_FRAGMENT:true });
  // Rebuild only recognized YouTube embeds with attributes controlled by the reader.
  for(const source of clean.querySelectorAll('iframe')) {
    let videoId;
    try {
      const url=new URL(source.getAttribute('src'));
      if(url.protocol==='https:'&&['www.youtube.com','www.youtube-nocookie.com'].includes(url.hostname)&&!url.port&&!url.username&&!url.password)videoId=url.pathname.match(/^\/embed\/([A-Za-z0-9_-]{11})$/)?.[1];
    } catch {}
    if(!videoId){source.remove();continue;}
    const frame=document.createElement('iframe');
    frame.src=`https://www.youtube-nocookie.com/embed/${videoId}`;
    frame.className='book-video'+(videoId==='ZktxO6adTnI'?' book-video-audiobook':'');frame.title=t("ui-youtube-video-player");frame.loading='lazy';
    frame.setAttribute('allow','accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share');
    frame.setAttribute('referrerpolicy','strict-origin-when-cross-origin');frame.setAttribute('allowfullscreen','');
    const shell=document.createElement('div');
    shell.className='book-video-shell';shell.append(frame);
    source.replaceWith(shell);
  }
  for(const el of clean.querySelectorAll('[src],[href]')) {
    const attr = el.hasAttribute('src') ? 'src':'href';
    const val = el.getAttribute(attr);
    if(!val) continue;
    if(attr === 'href' && val.startsWith('#')) continue;
    try {
      const url = new URL(val,base || location.href);
      if(!['http:','https:','blob:','data:'].includes(url.protocol) || (url.protocol === 'data:' && !((attr === 'src' || (el.localName === 'image' && attr === 'href')) && /^data:image\//i.test(val)))) el.removeAttribute(attr);
      else if(base) el.setAttribute(attr,url.href);
      if(attr === 'href' && ['http:','https:'].includes(url.protocol) && url.origin !== location.origin) { el.setAttribute('target','_blank'); el.setAttribute('rel','noopener noreferrer'); }
    } catch { el.removeAttribute(attr); }
  }
  if(prepareImage)for(const image of clean.querySelectorAll('img'))prepareImage(image);
  const holder = document.createElement('div'); holder.append(clean); return holder.innerHTML;
}
export const slug = s => String(s).toLowerCase().normalize('NFC').replace(/[^\p{L}\p{N}\s-]/gu,'').trim().replace(/\s+/g,'-') || 'section';
export const escape = s => String(s).replace(/[&<>"']/g,c=>({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
export function stableBlocks(html, chapterId) {
  const holder = document.createElement('div'); holder.innerHTML = safeHtml(html);
  let n=0;
  for(const el of holder.querySelectorAll('p,li,blockquote,pre,h2,h3,h4,h5,h6,figure,aside')) {
    if(!el.id) el.id=`${chapterId}-b${++n}`;
  }
  return holder.innerHTML;
}
export async function textDocument(source,options={}) {
  return documentFromHtml(await textHtml(source,options.markdown??true),options);
}
export async function fileDocument(file,options={}) {
  return documentFromHtml(await fileHtml(file,options.markdown??true),options);
}
async function documentFromHtml(html,{title=t("ui-untitled-reading"),markdown=true,id='text',language='en'}={}) {
  const holder=document.createElement('div');holder.innerHTML=safeHtml(html);
  const nodes=[...holder.childNodes].map(node=>node.nodeName==='H1'?{heading:true,title:node.textContent,slug:slug(node.textContent)}:{html:node.nodeType===3?escape(node.textContent):node.outerHTML||''});
  const chapters=await policy('importChapters',{title,nodes});
  for(const chapter of chapters){chapter.html=stableBlocks(chapter.html,chapter.id);chapter.text=htmlText(chapter.html);}
  return policy('importBook',{id,title,markdown,language,chapters});
}
export function htmlText(html) { const el=document.createElement('div'); el.innerHTML=html; return el.textContent || ''; }
export const documentMetadata = ({coverBlob,...book}) => policy('metadata',book);
